import React, { useEffect, useState } from 'react';
import {
  Camera, MapPin, Calendar, Stethoscope, BookOpen, ArrowRight,
  ExternalLink, Smartphone, Laptop2, Lock, Loader2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

/**
 * VisualWalkthrough — scroll-as-you-fill, screenshot-led guide for new
 * Gold-tier doctors. Designed to be opened on a phone while the doctor
 * fills the actual form on a laptop.
 *
 * Source of truth for the screenshots: `frontend/public/walkthrough/step-N.png`,
 * regenerated by `scripts/capture_walkthrough.py` on every preview deploy.
 * The Python script signs in as a seeded "demo Gold doctor", navigates
 * each tab, injects a red-dotted-circle + arrow SVG overlay on the next
 * button to click, and saves the PNG. Means the visual guide can NEVER
 * drift out of sync with the live UI.
 *
 * Public route — no auth — so doctors can share the URL with peers.
 */
const STEPS = [
  {
    n: 1,
    title: 'Tell patients about you',
    time: '3 min',
    icon: Camera,
    image: 1,
    body: (
      <>
        Open the <strong>Profile</strong> tab. Add your portrait photo, NMC / state council registration
        number, a 1-line tagline (e.g. "Cardiologist with 12 years' bypass experience"),
        a 2-3 line bio in your own voice, and pick your specialisation(s) from the list.
        Tap <strong>Save Profile</strong> at the bottom.
      </>
    ),
    cta: { label: 'Open my Profile tab', href: '/dashboard?tab=profile' },
  },
  {
    n: 2,
    title: 'Add your clinic location',
    time: '1 min',
    icon: MapPin,
    image: 2,
    body: (
      <>
        Open the <strong>Clinics & Hospital Infra</strong> tab. Tap <strong>Add Location</strong>.
        Type your clinic name in the search box — the dropdown shows real verified addresses.
        One click and the form auto-fills with the correct pincode, city, state, and map pin.
      </>
    ),
    cta: { label: 'Add my clinic now', href: '/dashboard?tab=locations' },
  },
  {
    n: 3,
    title: 'Set your OPD hours',
    time: '2 min',
    icon: Calendar,
    image: 3,
    body: (
      <>
        Open the <strong>Schedule</strong> tab. Tap <strong>Add Availability</strong>.
        Pick days of the week, morning + evening time slots per clinic, and slot duration.
        Patients see real available slots on the booking widget on your public profile.
      </>
    ),
    cta: { label: 'Set my hours', href: '/dashboard?tab=schedule' },
  },
  {
    n: 4,
    title: 'Pick your procedures + prices',
    time: '2 min',
    icon: Stethoscope,
    image: 4,
    body: (
      <>
        Open the <strong>Procedures</strong> tab. Tap <strong>Load My Catalog</strong>.
        30+ ready procedures pre-load with descriptions and price ranges. Delete the ones you
        don't do; set your own price on the rest.
      </>
    ),
    cta: { label: 'Load my catalog', href: '/dashboard?tab=procedures' },
  },
  {
    n: 5,
    title: 'Make it sound like you',
    time: '3 min',
    icon: BookOpen,
    image: 5,
    body: (
      <>
        Back to the <strong>Profile</strong> tab. Re-write the AI draft in your own voice —
        add a personal anecdote, change a phrase, soften the tone. Google needs ≥ 40% personal
        edits before it ranks your page. This is the #1 SEO lever.
      </>
    ),
    cta: { label: 'Edit my drafts', href: '/dashboard?tab=profile' },
  },
];

function EmailGuideLink() {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [msg, setMsg] = useState('');
  const send = async () => {
    setState('sending'); setMsg('');
    try {
      const r = await api.post('/private/email-onboarding-guide');
      setState('sent');
      setMsg(`Sent to ${r.data.to}. Check your inbox in ~30 seconds.`);
    } catch (e) {
      setState('error');
      setMsg(e.response?.data?.detail || 'Failed to send. Try again.');
    }
  };
  return (
    <div className="mt-5 flex flex-col items-center gap-1.5">
      <button
        type="button"
        onClick={send}
        disabled={state === 'sending' || state === 'sent'}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 underline-offset-2 hover:underline disabled:opacity-60"
        data-testid="email-guide-button"
      >
        {state === 'sending' && <><Loader2 size={12} className="animate-spin" /> Sending…</>}
        {state === 'sent' && <>✓ Guide emailed to you</>}
        {(state === 'idle' || state === 'error') && <>Email me the 2-page reference guide <ArrowRight size={12} /></>}
      </button>
      {msg && (
        <p className={`text-[11px] ${state === 'error' ? 'text-red-600' : 'text-slate-500'}`} data-testid="email-guide-msg">{msg}</p>
      )}
    </div>
  );
}

function AuthedImage({ step, alt, eager }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let revokedUrl = null;
    let active = true;
    api.get(`/private/walkthrough/step-${step}.png`, { responseType: 'blob' })
      .then((r) => {
        if (!active) return;
        revokedUrl = URL.createObjectURL(r.data);
        setSrc(revokedUrl);
      })
      .catch(() => active && setErr(true));
    return () => { active = false; if (revokedUrl) URL.revokeObjectURL(revokedUrl); };
  }, [step]);
  if (err) return (
    <div className="aspect-[16/10] bg-slate-100 flex items-center justify-center text-xs text-slate-400">
      Screenshot not available
    </div>
  );
  if (!src) return (
    <div className="aspect-[16/10] bg-slate-100 flex items-center justify-center text-xs text-slate-400">
      <Loader2 size={20} className="animate-spin" />
    </div>
  );
  return (
    <img src={src} alt={alt} loading={eager ? 'eager' : 'lazy'}
      className="w-full h-auto block" data-testid={`walkthrough-img-${step}`} />
  );
}

export default function VisualWalkthrough() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Suppress search-engine indexing while logged-in doctors view this page.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // Auth gate — doctors only. Patients and anon users are redirected to login
  // (preserving the intended destination so we deep-link back post-auth).
  useEffect(() => {
    if (loading) return;
    if (!user) navigate('/login?next=/visual-walkthrough', { replace: true });
    else if (user.role !== 'doctor') navigate('/', { replace: true });
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== 'doctor') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-slate-500 gap-3" data-testid="walkthrough-auth-gate">
        <Lock size={28} />
        <p className="text-sm">Doctor sign-in required to view the walkthrough.</p>
        <Link to="/login?next=/visual-walkthrough" className="text-sky-600 text-sm font-medium hover:underline">
          Sign in as a doctor →
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <header className="max-w-3xl mx-auto px-5 pt-8 pb-6 text-center">
        <p className="text-[11px] uppercase tracking-[0.22em] text-sky-600 font-bold">Gold plan · Visual walkthrough</p>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Open this on your phone.<br />Fill the form on your laptop.
        </h1>
        <p className="text-sm text-slate-600 mt-3 max-w-xl mx-auto">
          Five screenshots showing exactly which button to tap, in order.
          11 minutes from here to a live, indexable profile on Google.
        </p>
        <div className="mt-5 flex items-center justify-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-full">
            <Smartphone size={12} /> On phone
          </span>
          <ArrowRight size={14} className="text-slate-400" />
          <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-full">
            <Laptop2 size={12} /> Filled on laptop
          </span>
        </div>
        <EmailGuideLink />
      </header>

      {/* Steps */}
      <main className="max-w-3xl mx-auto px-4 pb-16">
        {STEPS.map((s, idx) => {
          const Icon = s.icon;
          return (
            <section
              key={s.n}
              className="mt-10 first:mt-0"
              data-testid={`walkthrough-step-${s.n}`}
            >
              <div className="flex items-baseline gap-3 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-sky-600 text-white flex items-center justify-center font-bold shrink-0 shadow-sm">
                  {s.n}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon size={16} className="text-sky-600" />
                    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {s.title}
                    </h2>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                      ~ {s.time}
                    </span>
                  </div>
                </div>
              </div>

              {/* Screenshot — fetched from auth-gated endpoint with per-doctor
                  watermark; rendered via blob URL so the JWT header travels. */}
              <figure className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white">
                <AuthedImage step={s.image} alt={`Step ${s.n}: ${s.title}`} eager={idx === 0} />
              </figure>

              <p className="text-sm text-slate-700 leading-relaxed mt-4 px-1">{s.body}</p>

              <a
                href={s.cta.href}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors"
                data-testid={`walkthrough-cta-${s.n}`}
              >
                {s.cta.label} <ArrowRight size={14} />
              </a>

              {idx < STEPS.length - 1 && (
                <div className="mt-10 flex justify-center">
                  <div className="w-px h-12 bg-gradient-to-b from-sky-300 to-transparent" />
                </div>
              )}
            </section>
          );
        })}

        {/* Finish */}
        <section className="mt-14 rounded-3xl bg-gradient-to-br from-emerald-50 to-sky-50 border-2 border-emerald-200 p-7 text-center">
          <h3 className="text-xl font-bold text-emerald-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            That's it — you're live.
          </h3>
          <p className="text-sm text-slate-700 mt-2 max-w-md mx-auto">
            The dashboard's celebration card will give you a one-tap WhatsApp share. Paste your
            link in 3 patient groups + your alumni group on day one — those shares are the single
            highest-leverage thing you can do for Google ranking.
          </p>
          <a
            href="/dashboard"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full px-6 py-3 bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
            data-testid="walkthrough-go-dashboard"
          >
            Open my dashboard <ExternalLink size={14} />
          </a>
        </section>

        <footer className="mt-10 text-center text-[11px] text-slate-400">
          Screenshots auto-regenerate on every preview deploy. Last refresh: today.
        </footer>
      </main>
    </div>
  );
}
